// Driver page — KYC (CDL scan, medical card, training certs)
// Self-service identity wallet for drivers
// Camera capture → AI extraction (future) → hashed into driver wallet

import { getAllDrivers, addDriver } from '../stores/db.js';
import { showToast } from '../app.js';

export async function renderDriver(container, ctx) {
  const drivers = await getAllDrivers(ctx.carrierDOT);

  const isSelfService = ctx.role === 'driver' || ctx.role === 'owner-operator';

  container.innerHTML = `
    <div class="page">
      <div class="page-title">${isSelfService ? 'My Compliance ID' : 'Drivers'}</div>

      ${isSelfService ? renderSelfService(ctx) : renderDriverList(drivers, ctx)}
    </div>
  `;
}

function renderSelfService(ctx) {
  return `
    <div class="card">
      <div class="card-title">${ctx.userName || 'Driver'}</div>
      <div class="card-meta">Carrier DOT# ${ctx.carrierDOT || 'Not set'}</div>
    </div>

    <div class="card">
      <div class="card-title" style="margin-bottom: 12px;">Documents</div>
      <p style="color: var(--text-dim); font-size: 0.85em; margin-bottom: 12px;">
        Photograph each document. AI will extract the data. Everything is hashed and stored in your driver wallet.
      </p>

      <div class="capture-row" style="flex-direction: column;">
        <div class="capture-btn" onclick="window._captureDoc('cdl')" style="padding: 16px;">
          <div style="font-size: 1.2em;">🪪 Scan CDL</div>
          <div style="font-size: 0.8em; color: var(--text-dim); margin-top: 4px;">Commercial Driver's License — front photo</div>
        </div>

        <div class="capture-btn" onclick="window._captureDoc('medical')" style="padding: 16px;">
          <div style="font-size: 1.2em;">🏥 Medical Certificate</div>
          <div style="font-size: 0.8em; color: var(--text-dim); margin-top: 4px;">DOT medical examiner's card</div>
        </div>

        <div class="capture-btn" onclick="window._captureDoc('drug_test')" style="padding: 16px;">
          <div style="font-size: 1.2em;">🧪 Drug/Alcohol Clearance</div>
          <div style="font-size: 0.8em; color: var(--text-dim); margin-top: 4px;">Clearinghouse result or C/TPA letter</div>
        </div>

        <div class="capture-btn" onclick="window._captureDoc('training')" style="padding: 16px;">
          <div style="font-size: 1.2em;">📜 Training Certificate</div>
          <div style="font-size: 0.8em; color: var(--text-dim); margin-top: 4px;">Hazmat, defensive driving, load securement, etc.</div>
        </div>

        <div class="capture-btn" onclick="window._captureDoc('mvr')" style="padding: 16px;">
          <div style="font-size: 1.2em;">📋 Motor Vehicle Record</div>
          <div style="font-size: 0.8em; color: var(--text-dim); margin-top: 4px;">State DMV driving record</div>
        </div>
      </div>
    </div>

    <div id="driver-docs-list" style="margin-top: 12px;"></div>

    <div class="card" style="margin-top: 12px; text-align: center;">
      <div style="color: var(--text-dim); font-size: 0.85em;">
        Your compliance wallet link will be shareable with any broker.
        They see your compliance score — not your personal documents.
      </div>
    </div>
  `;
}

function renderDriverList(drivers, ctx) {
  return `
    <button class="btn btn-primary btn-small" onclick="window._addDriverForm()" style="margin-bottom: 16px;">+ Add Driver</button>

    ${drivers.length === 0
      ? '<div class="card"><p style="color: #888; text-align: center;">No drivers registered. Add drivers to manage their compliance.</p></div>'
      : drivers.map(d => `
        <div class="card">
          <div style="display: flex; justify-content: space-between;">
            <div>
              <div class="card-title">${d.name || 'Unknown'}</div>
              <div class="card-meta">CDL: ${d.cdlNumber || 'Not scanned'} | ${d.cdlState || ''}</div>
              ${d.medicalExpiry ? `<div class="card-meta">Medical expires: ${d.medicalExpiry}</div>` : ''}
            </div>
            <div>
              <span class="badge ${d.complianceStatus === 'current' ? 'badge-pass' : 'badge-pending'}">
                ${d.complianceStatus || 'Incomplete'}
              </span>
            </div>
          </div>
          <div class="card-meta" style="margin-top: 4px;">
            Docs: ${[d.hasCDL && 'CDL', d.hasMedical && 'Medical', d.hasDrugTest && 'Drug', d.hasTraining && 'Training'].filter(Boolean).join(', ') || 'None'}
          </div>
        </div>
      `).join('')
    }

    <div id="add-driver-form" style="display: none;">
      <div class="card" style="border-color: var(--accent);">
        <div class="card-title" style="margin-bottom: 12px;">Add Driver</div>
        <div class="input-group">
          <label>Driver Name</label>
          <input type="text" id="driver-name" placeholder="Full name">
        </div>
        <div class="input-group">
          <label>Phone</label>
          <input type="tel" id="driver-phone" placeholder="(555) 123-4567">
        </div>
        <div class="input-group">
          <label>CDL Number (optional — can scan later)</label>
          <input type="text" id="driver-cdl" placeholder="CDL number">
        </div>
        <div class="input-group">
          <label>CDL State</label>
          <input type="text" id="driver-cdl-state" placeholder="e.g. IA" maxlength="2">
        </div>
        <div style="display: flex; gap: 8px;">
          <button class="btn btn-primary" onclick="window._saveDriver()">Save Driver</button>
          <button class="btn btn-secondary" onclick="document.getElementById('add-driver-form').style.display='none'">Cancel</button>
        </div>
      </div>
    </div>
  `;
}

// ─── Event Handlers ─────────────────────────────────────────────────

window._captureDoc = async (docType) => {
  const labels = {
    cdl: 'CDL (front)',
    medical: 'Medical Certificate',
    drug_test: 'Drug/Alcohol Clearance',
    training: 'Training Certificate',
    mvr: 'Motor Vehicle Record',
  };

  try {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        // Store photo and show confirmation
        // TODO: AI extraction of CDL fields, medical expiry, etc.
        showToast(`${labels[docType]} captured — AI extraction coming soon`);

        const docList = document.getElementById('driver-docs-list');
        if (docList) {
          docList.innerHTML += `
            <div class="card" style="display: flex; gap: 12px; align-items: center;">
              <img src="${reader.result}" style="width: 60px; height: 40px; object-fit: cover; border-radius: 4px;">
              <div>
                <div style="font-weight: 600; color: #fff; font-size: 0.9em;">${labels[docType]}</div>
                <div class="card-meta">Captured ${new Date().toLocaleString()}</div>
              </div>
              <span class="badge badge-pass" style="margin-left: auto;">Captured</span>
            </div>
          `;
        }
      };
      reader.readAsDataURL(file);
    };
    input.click();
  } catch (e) {
    showToast('Camera not available');
  }
};

window._addDriverForm = () => {
  document.getElementById('add-driver-form').style.display = 'block';
};

window._saveDriver = async () => {
  const name = document.getElementById('driver-name').value.trim();
  if (!name) { showToast('Enter driver name'); return; }

  const driver = {
    name,
    phone: document.getElementById('driver-phone').value.trim(),
    cdlNumber: document.getElementById('driver-cdl').value.trim(),
    cdlState: document.getElementById('driver-cdl-state').value.trim().toUpperCase(),
    carrierDOT: localStorage.getItem('fp_carrier_dot') || '',
    complianceStatus: 'incomplete',
    hasCDL: false,
    hasMedical: false,
    hasDrugTest: false,
    hasTraining: false,
  };

  await addDriver(driver);
  showToast(`${name} added`);
  // Re-render
  const content = document.getElementById('page-content');
  const ctx = { role: localStorage.getItem('fp_role'), carrierDOT: localStorage.getItem('fp_carrier_dot'), userName: localStorage.getItem('fp_user_name') };
  renderDriver(content, ctx);
};
